import { ChangeEvent } from 'react';
import { Spinner } from '@chakra-ui/react';
import AutoFixHighIcon from '@mui/icons-material/AutoFixHigh';
import DeleteIcon from '@mui/icons-material/Delete';
import EditIcon from '@mui/icons-material/Edit';
import SaveIcon from '@mui/icons-material/Save';
import { format } from 'date-fns';
import TransparentButton from '@/trio-ui/TransparentButton';
import UserInitials from '@/trio-ui/UserInitials';
import { User } from '@/types/api';
import { getFirstAndLastNamesSplitted } from '@/utils/format';
import * as Styles from './InterviewReview.styles';
import DialogBox from '../DialogBox';
import TextEditor from '../TextEditor';

export type Score = {
  id: string;
  type: string;
  name: string;
  label: string;
  score: string;
  prevScore?: string;
};

export type InterviewReviewComponentProps = {
  isEditing: boolean;
  canSave: boolean;
  scores: Score[];
  totalScore: number;
  currentScore: number;
  note: string;
  reviewer?: User;
  reviewedAt?: Date;
  isDeleteModalOpen: boolean;
  isActionLoading: boolean;
  ratingQuantity: number;
  onChangeScore: (event: ChangeEvent<HTMLInputElement>) => void;
  onChangeNote: (value: string) => void;
  onToggleIsEditing: () => void;
  onSaveReview: () => void;
  onGenerateReviewWithAI: () => void;
  onDeleteReview: () => void;
  onCancelEdition: () => void;
  onToggleDeleteModal: (event?: any) => void;
};

export default function InterviewReviewComponent({
  reviewer,
  reviewedAt,
  isEditing,
  canSave,
  isActionLoading,
  note,
  scores,
  totalScore,
  currentScore,
  isDeleteModalOpen,
  ratingQuantity,
  onChangeScore,
  onChangeNote,
  onSaveReview,
  onGenerateReviewWithAI,
  onToggleIsEditing,
  onDeleteReview,
  onToggleDeleteModal,
  onCancelEdition,
}: InterviewReviewComponentProps): JSX.Element {
  const [firstName, lastName] = getFirstAndLastNamesSplitted(reviewer?.name);

  return (
    <>
      <Styles.Box isReviewed={!!reviewedAt}>
        {!!reviewedAt && (
          <Styles.ReviewInfos>
            <UserInitials size={24} firstName={firstName} lastName={lastName} />
            <Styles.ReviewerName>{reviewer?.name}</Styles.ReviewerName>
            <Styles.ReviewTime>{format(reviewedAt, 'MMM d HH:MM  a')}</Styles.ReviewTime>
          </Styles.ReviewInfos>
        )}

        <TextEditor
          value={note}
          onChangeText={onChangeNote}
          isEditing={isEditing}
          hideButtons
          hideEditButton
        />

        <Styles.InputsBox>
          <Styles.InputsRow>
            {scores.map((score) => (
              <Styles.InputWrapper key={score.name} inputListSize={scores.length}>
                <Styles.CustomInputRating
                  key={score.name}
                  name={score.name}
                  label={score.label}
                  value={score.score}
                  disabled={!isEditing}
                  onChangeHandler={onChangeScore}
                  ratingQuantity={ratingQuantity}
                />
              </Styles.InputWrapper>
            ))}
          </Styles.InputsRow>

          <Styles.ScoreBox>
            Reviewer Score:{' '}
            <span>
              {currentScore}/{totalScore}
            </span>
          </Styles.ScoreBox>
        </Styles.InputsBox>

        <Styles.ButtonsBox>
          {isEditing ? (
            <>
              {isActionLoading ? (
                <Spinner size="sm" />
              ) : (
                <>
                  <TransparentButton
                    isBlueButton
                    icon={SaveIcon}
                    onClick={onSaveReview}
                    disabled={!canSave || isActionLoading}
                  >
                    Save review
                  </TransparentButton>
                  {!reviewedAt && (
                    <TransparentButton
                      isBlueButton
                      icon={AutoFixHighIcon}
                      onClick={onGenerateReviewWithAI}
                      disabled={isActionLoading}
                    >
                      Generate with AI
                    </TransparentButton>
                  )}
                  <TransparentButton
                    icon={DeleteIcon}
                    isRedButton
                    onClick={onCancelEdition}
                    disabled={isActionLoading}
                  >
                    Cancel
                  </TransparentButton>
                </>
              )}
            </>
          ) : (
            <>
              <TransparentButton
                icon={EditIcon}
                onClick={onToggleIsEditing}
                disabled={isActionLoading}
              >
                Edit review
              </TransparentButton>

              <TransparentButton
                icon={DeleteIcon}
                isRedButton
                onClick={onToggleDeleteModal}
                disabled={isActionLoading}
              >
                Delete review
              </TransparentButton>
            </>
          )}
        </Styles.ButtonsBox>
      </Styles.Box>

      <DialogBox
        isOpen={isDeleteModalOpen}
        onCancelHandler={onToggleDeleteModal}
        onConfirmHandler={onDeleteReview}
      />
    </>
  );
}
